import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { isAxiosError } from 'axios'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import IProps from 'app/interfaces/IProps'
import { activateMaintenance } from 'app/store/slice/maintenanceSlice'
import { setAuth } from 'app/store/slice/authSlice'
import { Role } from 'app/types/types'
import { loginRequest } from 'authConfig'
import IAppServiceValue from './interfaces/IAppServiceValue'
import AuthenticationService from '../api/authentication/authentication.service'
import IAllowedGroup from './interfaces/IAllowedGroup'
import IDomainDetail from './interfaces/IDomainDetail'
import DomainServices from '../api/common/domain.service'
import { useAppDispatch } from '../hooks/store-hooks'
import { useGetToken } from '../hooks/useGetToken'
import { useNotification } from '../hooks/useNotification'
import {
  clearAuthRedirectInProgress,
  isAuthRedirectInProgress,
  markAuthRedirectInProgress,
} from '../utils/authRedirect'
import {
  getMaintenanceMessage,
  isMaintenanceModeError,
} from '../utils/maintenanceMode'
import IPageRoute from './interfaces/IPageRoute'
import { usePersistentState } from '../utils/usePersistentState'
import ITemplateConfiguraionState from './interfaces/ITemplateConfiguraionState'
import { AuthStartupStatus, SetTemplateConfigurationStateMethod } from './types'
import IUser from './interfaces/IUser'

const AppService = createContext<Partial<IAppServiceValue>>({})

export const useAppService = () => useContext(AppService)

export function AppServiceProvider({ children }: IProps) {
  const [loading, setLoading] = useState<boolean>(true)
  const [authStartupStatus, setAuthStartupStatus] = useState<AuthStartupStatus>(
    'bootstrapping_identity'
  )
  const [templateConfigurationState, setTCState] =
    useState<ITemplateConfiguraionState>()
  const [isAuthorised, setAuthorised] = useState<boolean>(true)
  const [allowedGroups, setallowedGroups] = useState<IAllowedGroup[]>([])
  const [currentDomainDetails, setCurrentDomainDetails] = usePersistentState(
    'currentDomain',
    {
      domain: '',
      region: '',
      division: [],
      storageLocation: [],
      storageAccount: [],
      domainDivisionRegionId: [],
    }
  )
  const [domains, setDomains] = useState<IDomainDetail[]>([])
  const [user, setuser] = useState<IUser>()
  const hasBootstrappedAuth = useRef(false)
  const { instance } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const getToken = useGetToken()
  const dispatch = useAppDispatch()
  const { errorNotification, sendNotification } = useNotification()

  const setTemplateConfigurationState =
    useCallback<SetTemplateConfigurationStateMethod>(
      (props: ITemplateConfiguraionState | undefined) => {
        setTCState(props)
      },
      []
    )

  const getRolesForCurrentDomain = useMemo<Role[]>(
    () =>
      allowedGroups
        .filter(
          (value) =>
            value.domainDivisionRegionId ===
            parseInt(currentDomainDetails.domainDivisionRegionId[0], 10)
        )
        .map((value) => value.role),
    [allowedGroups, currentDomainDetails.domainDivisionRegionId]
  )

  const ifUserCanRead = useMemo<boolean>(
    () =>
      allowedGroups.some(
        (value) =>
          value.domainDivisionRegionId ===
            parseInt(currentDomainDetails.domainDivisionRegionId[0], 10) &&
          (value.permission === 'read' || value.permission === 'read/write')
      ),
    [allowedGroups, currentDomainDetails.domainDivisionRegionId]
  )

  const ifUserCanWrite = useMemo<boolean>(
    () =>
      allowedGroups.some(
        (value) =>
          value.domainDivisionRegionId ===
            parseInt(currentDomainDetails.domainDivisionRegionId[0], 10) &&
          (value.permission === 'write' || value.permission === 'read/write')
      ),
    [allowedGroups, currentDomainDetails.domainDivisionRegionId]
  )

  const getPages = useMemo<IPageRoute[]>(() => {
    const result = []
    if (
      getRolesForCurrentDomain.some(
        (value) =>
          value === 'admin' ||
          value === 'dev' ||
          value === 'normal_user' ||
          value === 'maintenance'
      )
    )
      result.push({ key: 'home', label: 'Home', route: '/' })

    if (
      getRolesForCurrentDomain.some(
        (value) =>
          value === 'admin' || value === 'dev' || value === 'maintenance'
      )
    ) {
      result.push({
        key: 'template',
        label: 'Template Configuration',
        route: '/template-configuration',
      })

      result.push({
        key: 'history',
        label: 'Template History',
        route: '/template-history',
      })
    }

    // result.push({
    //   key: 'dataManipulation',
    //   label: 'Data Manipulation',
    //   route: '/data-manipulation',
    // })

    return result
  }, [getRolesForCurrentDomain])

  useEffect(() => {
    if (hasBootstrappedAuth.current) {
      return
    }
    hasBootstrappedAuth.current = true

    const authCalls = async () => {
      setAuthStartupStatus('acquiring_token_silent')

      let interactionRequired = false
      const accessToken = await getToken({
        onInteractionRequired: () => {
          interactionRequired = true
        },
      })

      if (!accessToken) {
        if (
          (interactionRequired || isAuthenticated) &&
          !isAuthRedirectInProgress()
        ) {
          setAuthStartupStatus('redirecting_to_sso')
          sendNotification({
            message: 'Authentication token expired. Redirecting to SSO.',
            variant: 'warning',
          })
          markAuthRedirectInProgress()
          await instance.loginRedirect(loginRequest)
        }
        return
      }

      setAuthStartupStatus('validating_backend_token')
      const authResp = await AuthenticationService.login(accessToken)

      setuser(authResp.data.data)
      setallowedGroups(authResp.data.data.allowedGroups)
      const domainIds = authResp.data.data.domainDivisionRegionId
      dispatch(setAuth({ auth: authResp.data.data }))

      const domainResp = await DomainServices.getDomainDetails(domainIds)
      setDomains(domainResp.data.data)
      if (currentDomainDetails && currentDomainDetails.domain.length === 0) {
        setCurrentDomainDetails(domainResp.data.data[0])
      }

      setAuthStartupStatus('authorized')
      clearAuthRedirectInProgress()
      setLoading(false)
    }

    authCalls().catch((err) => {
      if (isMaintenanceModeError(err)) {
        setAuthStartupStatus('maintenance_mode')
        clearAuthRedirectInProgress()
        dispatch(
          activateMaintenance({
            message: getMaintenanceMessage(err),
          })
        )
        setLoading(false)
        return
      }

      if (isAxiosError(err) && err.response?.status === 401) {
        setAuthStartupStatus('not_mapped_or_unauthorized')
        clearAuthRedirectInProgress()
        setAuthorised(false)
      } else {
        clearAuthRedirectInProgress()
        errorNotification()
      }
      setLoading(false)
      console.error(err)
    })
  }, [
    currentDomainDetails,
    dispatch,
    errorNotification,
    getToken,
    instance,
    isAuthenticated,
    setCurrentDomainDetails,
    sendNotification,
  ])

  const contextValue = useMemo(
    () => ({
      loading,
      authStartupStatus,
      getRolesForCurrentDomain,
      ifUserCanRead,
      ifUserCanWrite,
      allowedGroups,
      currentDomainDetails,
      isAuthorised,
      domains,
      setCurrentDomainDetails,
      getPages,
      templateConfigurationState,
      user,
      setTemplateConfigurationState,
    }),
    [
      loading,
      authStartupStatus,
      getRolesForCurrentDomain,
      ifUserCanRead,
      ifUserCanWrite,
      allowedGroups,
      currentDomainDetails,
      isAuthorised,
      domains,
      setCurrentDomainDetails,
      getPages,
      templateConfigurationState,
      user,
      setTemplateConfigurationState,
    ]
  )

  return (
    <AppService.Provider value={contextValue}>{children}</AppService.Provider>
  )
}
